import { blueGrey, common } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { RaThemeOptions } from 'react-admin';

export const craftBlueMain = '#00AEFF';
export const craftBlueDark = '#0076EA';
export const craftBlueLight = '#00DFFF';

export const { black, white } = common;

export const appLightBlue = '#C2E1FF';
export const appDarkBlue = '#0052A3';
export const appPurple = '#5434DC';
export const appRed = '#E34242';
export const darkGrey333 = '#333';
export const darkBlueGrey = '#555866';
export const mediumBlueGrey = '#8A8C95';
export const blackBlue = '#21242b';
export const backgroundBlack = '#121212';
export const backgroundLightGrey = '#F5F5F5';
export const surfaceBlack = '#1E1E1E';
export const textWhite = '#E9EBF5';
export const textMediumGrey = '#ADADAD';
export const overlayDark = '#0000000A';
export const overlayLight = '#FFFFFF14';
export const lightThemeBackgroundLightRed1 = '#FCD4D4';
export const lightThemeBackgroundLightRed2 = '#FFC2C2';
export const darkThemeBackgroundDarkRed1 = '#3D1414';
export const darkThemeBackgroundDarkRed2 = '#4D1919';

const commonThemeOptions: RaThemeOptions = {
  typography: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 500,
    fontWeightBold: 500,
    code: {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      border: `1px solid ${blueGrey['100']}`,
      padding: '0 0.25rem',
      display: 'inline-block',
      borderRadius: 4,
    },
    body2: {
      lineHeight: 1.5,
      fontSize: '1rem',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        /**
         * Aligns small MUI/React Admin height
         */
        sizeSmall: {
          lineHeight: 1.5,
        },
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'medium',
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          // prevent List header text from wrapping
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: 8,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          code: 'code',
        },
      },
    },
  },
};

// eslint-disable-next-line import/no-mutable-exports
export let lightTheme = createTheme(
  deepmerge(commonThemeOptions, {
    palette: {
      mode: 'light',
      primary: {
        main: craftBlueDark,
        light: appLightBlue,
        dark: appDarkBlue,
        contrastText: white,
      },
      secondary: {
        main: appPurple,
      },
      info: {
        main: craftBlueMain,
      },
      error: {
        main: appRed,
      },
      background: {
        default: blueGrey['50'],
        paper: white,
      },
      border: {
        main: blueGrey['100'],
      },
      text: {
        primary: darkBlueGrey,
        secondary: mediumBlueGrey,
      },
    },
  }),
);

// create in steps for referencing, and better typing
lightTheme = createTheme(lightTheme, {
  typography: {
    code: {
      background: blueGrey['50'],
    },
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.RaMenuItemLink-active': {
            background: overlayDark,
          },
        },
      },
    },
    RaToolbar: {
      styleOverrides: {
        root: {
          background: backgroundLightGrey,
        },
      },
    },
  },
});

// eslint-disable-next-line import/no-mutable-exports
export let darkTheme = createTheme(
  deepmerge(commonThemeOptions, {
    palette: {
      mode: 'dark',
      primary: {
        main: craftBlueDark,
        light: craftBlueLight,
        dark: blackBlue,
        contrastText: white,
      },
      secondary: {
        main: appPurple,
      },
      info: {
        main: craftBlueMain,
      },
      error: {
        main: appRed,
      },
      background: {
        default: backgroundBlack,
        paper: surfaceBlack,
      },
      border: {
        main: darkGrey333,
      },
      text: {
        primary: textWhite,
        secondary: textMediumGrey,
      },
    },
  }),
);

// create in steps for referencing, and better typing
darkTheme = createTheme(darkTheme, {
  typography: {
    code: {
      borderColor: mediumBlueGrey,
      background: darkBlueGrey,
    },
  },
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          color: darkTheme.palette.primary.light,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.RaMenuItemLink-active': {
            background: overlayLight,
          },
        },
      },
    },
    RaToolbar: {
      styleOverrides: {
        root: {
          background: darkGrey333,
        },
      },
    },
  },
});
